import React from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, BarChart as BarChartIcon } from "@mui/icons-material";
import { TimeSeriesData } from "../../shared/types/dashboard";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface SalesChartProps {
  timeSeries: TimeSeriesData[];
  title?: string;
  height?: number;
  showGrid?: boolean;
  timeframe?: string;
}

const SalesChart: React.FC<SalesChartProps> = ({ timeSeries, title = "Sales Overview", height = 320, showGrid = true, timeframe = "7d" }) => {
  const FormatAmount = useFormatAmount()
  const totalSales = timeSeries.reduce((s, i) => s + (i.sales || 0), 0);
  const totalOrders = timeSeries.reduce((s, i) => s + (i.orders || 0), 0);
  const avgSales = timeSeries.length ? totalSales / timeSeries.length : 0;
  const avgOrders = timeSeries.length ? totalOrders / timeSeries.length : 0;

  const CustomTooltip = ({ active, payload, label }: any) =>
    active && payload?.length ? (
      <div className="bg-white p-3] rounded-lg shadow border border-gray-200">
        <p className="text-sm font-semibold mb-2">{label}</p>
        {payload.map((e: any, i: number) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="capitalize text-gray-600">{e.dataKey}</span>
            <span className="font-semibold text-gray-900">{FormatAmount(e.value)}</span>
          </div>
        ))}
      </div>
    ) : null;

  const formatY = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

  return (
    <Card className="w-full rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
      <CardContent className="p-2 sm:p-4">
        {/* Header */}
        <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <Box className="flex items-center gap-3">
            <Box className="p-2 bg-indigo-600 text-white rounded-lg">
              <BarChartIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" className="font-bold text-gray-900">
                {title}
              </Typography>
              {/* <Typography variant="body2" className="text-gray-600">
                Last {timeframe.replace("d", " days")}
              </Typography> */}
            </Box>
          </Box>

          <Box className="flex gap-6 text-center">
            <div>
              <Typography variant="body2" className="text-gray-500">
                Total Sales
              </Typography>
              <Typography variant="h6" className="font-bold text-indigo-600">
                {FormatAmount(totalSales)}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" className="text-gray-500">
                Total Orders
              </Typography>
              <Typography variant="h6" className="font-bold text-green-600">
                {totalOrders.toLocaleString()}
              </Typography>
            </div>
          </Box>
        </Box>

        {/* <Divider className="mb-4" /> */}

        {/* Chart */}
        <Box className="bg-gray-50 border border-gray-200 rounded-xl p-4" sx={{ height }}>
          {timeSeries.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeSeries} barGap={6}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />}
                <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatY} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" fill="#3b82f6" name="Sales" radius={[6, 6, 0, 0]} />
                <Bar dataKey="orders" fill="#10b981" name="Orders" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box className="flex flex-col items-center justify-center h-full text-gray-600">
              <TrendingUp className="text-4xl mb-2 text-gray-400" />
              <Typography variant="body1" className="font-medium">
                No Data Available
              </Typography>
              <Typography variant="body2" className="text-gray-500">
                Sales and orders will appear here once available.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        {timeSeries.length > 0 && (
          <Box className="grid grid-cols-2 gap-3 mt-4">
            <Box className="text-center bg-indigo-50 border border-indigo-100 rounded-lg p-2">
              <Typography variant="body2" className="text-indigo-600">
                Avg. Sales
              </Typography>
              <Typography variant="h6" className="font-bold text-indigo-900">
                {avgSales.toFixed(2)}
              </Typography>
            </Box>
            <Box className="text-center bg-green-50 border border-green-100 rounded-lg p-2">
              <Typography variant="body2" className="text-green-600">
                Avg. Orders
              </Typography>
              <Typography variant="h6" className="font-bold text-green-900">
                {avgOrders.toFixed(0)}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesChart;